import React, { useState, useEffect } from "react";
import { Search, ArrowLeft, GraduationCap, Calendar, MessageSquare, User, Droplet, Users, Phone, School, CreditCard, MapPin, FileEdit } from "lucide-react";
export default function StudentView({
  student,
  onBack
}) {
  const [studentDetails, setStudentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!student) return;

    // Use initially passed properties
    setStudentDetails(student.raw || student);
    const studentId = student.raw?._id || student._id;
    if (!studentId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`https://schoolcrm-fzcv.onrender.com/api/admissions/${studentId}`);
        if (res.ok) {
          const freshData = await res.json();
          setStudentDetails(freshData);
        } else {
          const fallbackRes = await fetch(`/api/admissions/${studentId}`);
          if (fallbackRes.ok) {
            const freshData = await fallbackRes.json();
            setStudentDetails(freshData);
          }
        }
      } catch (err) {
        console.error("Failed to fetch fresh student details:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [student]);
  if (!student) {
    return <div className="flex flex-col items-center justify-center h-96 bg-white rounded-3xl border border-slate-100 shadow-sm p-8 text-center">
        <span className="text-5xl mb-4"><Search className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
        <h3 className="text-xl font-bold text-slate-800">No Student Selected</h3>
        <p className="text-sm text-slate-500 mt-2">Please go back and select a student to view details.</p>
        <button onClick={onBack} className="mt-5 px-6 py-2.5 bg-amber-500 text-white rounded-xl font-semibold shadow-md hover:bg-amber-600 transition-all text-sm">
          <ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /> Back to Directory
        </button>
      </div>;
  }
  const raw = studentDetails || student.raw || {};

  // Extract fields with fallbacks
  const name = raw.studentName || student.name || "—";
  const rollNo = raw.rollNo || student.roll || "—";
  const className = raw.applyingClass || student.cls || "—";
  const section = raw.section || student.section || "A";
  const gender = raw.gender || "—";
  const dob = raw.dob || "—";
  const bloodGroup = raw.bloodGroup || "—";
  const religion = raw.religion || "—";
  const nationality = raw.nationality || "Indian";
  const motherTongue = raw.motherTongue || "—";
  const aadhaar = raw.aadhaar || "—";
  const photo = raw.photo || null;
  const fatherName = raw.fatherName || "—";
  const fatherOccupation = raw.fatherOccupation || "—";
  const fatherMobile = raw.fatherMobile || "—";
  const fatherAadhaar = raw.fatherAadhaar || "—";
  const motherName = raw.motherName || "—";
  const motherOccupation = raw.motherOccupation || "—";
  const motherMobile = raw.motherMobile || "—";
  const address = raw.address || "—";
  const city = raw.city || "—";
  const pincode = raw.pincode || "—";
  const prevSchool = raw.prevSchool || "—";
  const prevClass = raw.prevClass || "—";
  const prevBoard = raw.prevBoard || "—";
  const tcNumber = raw.tcNumber || "—";
  const status = raw.status || student.admissionStatus || "Enquiry";
  const enquiryDate = raw.enquiryDate || student.date || "—";
  const remarks = raw.remarks || "—";
  const totalFee = raw.totalFee || student.feeTotal || 0;
  const paidFee = raw.paid || student.feePaid || 0;
  const pendingFee = totalFee - paidFee;
  const paidPercentage = totalFee > 0 ? Math.min(Math.round(paidFee / totalFee * 100), 100) : 0;

  // Initials for avatar fallback
  const nameParts = name.trim().split(" ").filter(Boolean);
  const initials = nameParts.length >= 2 ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();

  // Status Colors Mapping
  const getStatusStyle = st => {
    const s = st.toLowerCase();
    if (s.includes("admitted") || s === "joined") return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (s.includes("pending") || s === "inprogress") return "bg-amber-100 text-amber-800 border-amber-200";
    if (s.includes("interview") || s === "approval") return "bg-blue-100 text-blue-800 border-blue-200";
    if (s.includes("not admitted") || s === "rejected") return "bg-rose-100 text-rose-800 border-rose-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };
  return <div className="w-full min-h-screen pb-12 animate-fadeIn">
      {/* Back Button */}
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800 mb-6 transition-all hover:translate-x-[-4px]">
        <span><ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Back to list
      </button>

      {/* ─── HERO HEADER BANNER ─── */}
      <div className="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-950 rounded-3xl p-6 md:p-8 text-white shadow-xl overflow-hidden mb-8">
        {/* Background Decorative Circles */}
        <div className="absolute top-[-20%] right-[-10%] w-72 h-72 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-[-30%] left-[-10%] w-96 h-96 rounded-full bg-emerald-500/5 blur-3xl pointer-events-none"></div>

        <div className="relative flex flex-col md:flex-row items-center gap-6 z-10">
          {/* Avatar / Photo Container */}
          <div className="relative">
            {photo ? <img src={photo} alt={name} className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover border-4 border-white/20 shadow-2xl transition-transform hover:scale-105 duration-300" /> : <div className={`w-24 h-24 md:w-28 md:h-28 rounded-2xl flex items-center justify-center font-extrabold text-3xl md:text-4xl shadow-2xl border-4 border-white/10 ${student.avBg || 'bg-gradient-to-br from-amber-400 to-orange-500'} ${student.avColor || 'text-white'}`}>
                {initials}
              </div>}
            <span className="absolute bottom-[-6px] right-[-6px] bg-amber-500 text-white font-bold text-[10px] px-2 py-0.5 rounded-full border-2 border-slate-900 shadow">
              STUDENT
            </span>
          </div>

          {/* Details */}
          <div className="text-center md:text-left flex-1">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-none">{name}</h1>
              <span className={`text-xs font-extrabold px-3 py-1 rounded-full border uppercase tracking-wider self-center md:self-auto ${getStatusStyle(status)}`}>
                {status}
              </span>
            </div>
            <p className="text-indigo-200/90 font-medium text-sm md:text-base mt-2">
              <GraduationCap className="inline w-[1em] h-[1em] align-[-0.125em]" /> {className} &nbsp;·&nbsp; Section {section} {raw.group && `(${raw.group})`}
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-4 text-xs font-semibold">
              <span className="bg-white/10 px-3 py-1.5 rounded-xl border border-white/5 backdrop-blur-md">
                🆔 Roll No: <strong className="text-amber-400">{rollNo}</strong>
              </span>
              <span className="bg-white/10 px-3 py-1.5 rounded-xl border border-white/5 backdrop-blur-md">
                <Calendar className="inline w-[1em] h-[1em] align-[-0.125em]" /> Enquiry Date: <strong>{enquiryDate}</strong>
              </span>
              {raw.medium && <span className="bg-white/10 px-3 py-1.5 rounded-xl border border-white/5 backdrop-blur-md">
                  <MessageSquare className="inline w-[1em] h-[1em] align-[-0.125em]" /> Medium: <strong>{raw.medium}</strong>
                </span>}
            </div>
          </div>
        </div>
      </div>

      {/* ─── DETAIL SECTIONS GRID ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Side: Personal Info & Previous School */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Personal Information */}
          <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
            <h3 className="text-md font-bold text-slate-800 border-b border-slate-50 pb-3.5 mb-4 flex items-center gap-2">
              <span><User className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[{
              label: "Full Name",
              val: name
            }, {
              label: "Gender",
              val: gender
            }, {
              label: "Date of Birth",
              val: dob
            }, {
              label: "Blood Group",
              val: bloodGroup,
              element: bloodGroup && bloodGroup !== "—" ? <span className="inline-flex items-center gap-1 font-bold text-red-600">
                      <Droplet className="inline w-[1em] h-[1em] align-[-0.125em]" /> {bloodGroup}
                    </span> : null
            }, {
              label: "Aadhaar Card No.",
              val: aadhaar
            }, {
              label: "Religion",
              val: religion
            }, {
              label: "Nationality",
              val: nationality
            }, {
              label: "Mother Tongue",
              val: motherTongue
            }].map((f, i) => <div key={i} className="bg-slate-50/50 hover:bg-slate-50 rounded-2xl p-3 border border-slate-100/50 transition-colors">
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{f.label}</div>
                  <div className="text-sm font-semibold text-slate-800 mt-1">{f.element || f.val}</div>
                </div>)}
            </div>
          </div>

          {/* Parents & Guardians Details */}
          <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
            <h3 className="text-md font-bold text-slate-800 border-b border-slate-50 pb-3.5 mb-4 flex items-center gap-2">
              <span><Users className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Family & Guardians
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Father card */}
              <div className="bg-slate-50/40 rounded-2xl p-4 border border-slate-100 hover:border-indigo-100 transition-all">
                <div className="text-xs font-bold text-indigo-700 uppercase tracking-widest mb-3 pb-1 border-b border-slate-100">
                  <User className="inline w-[1em] h-[1em] align-[-0.125em]" /> Father Details
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Name</div>
                    <div className="text-sm font-bold text-slate-800 mt-0.5">{fatherName}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Occupation</div>
                    <div className="text-xs font-semibold text-slate-600 mt-0.5">{fatherOccupation}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Mobile Number</div>
                    <div className="text-sm font-bold text-slate-800 mt-0.5"><Phone className="inline w-[1em] h-[1em] align-[-0.125em]" /> {fatherMobile}</div>
                  </div>
                  {fatherAadhaar && fatherAadhaar !== "—" && <div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase">Aadhaar Card No.</div>
                      <div className="text-xs font-semibold text-slate-700 mt-0.5">{fatherAadhaar}</div>
                    </div>}
                </div>
              </div>

              {/* Mother card */}
              <div className="bg-slate-50/40 rounded-2xl p-4 border border-slate-100 hover:border-pink-100 transition-all">
                <div className="text-xs font-bold text-pink-700 uppercase tracking-widest mb-3 pb-1 border-b border-slate-100">
                  <User className="inline w-[1em] h-[1em] align-[-0.125em]" /> Mother Details
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Name</div>
                    <div className="text-sm font-bold text-slate-800 mt-0.5">{motherName}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Occupation</div>
                    <div className="text-xs font-semibold text-slate-600 mt-0.5">{motherOccupation}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Mobile Number</div>
                    <div className="text-sm font-bold text-slate-800 mt-0.5">{motherMobile !== "—" ? `${motherMobile}` : "—"}</div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Previous Academic Background */}
          {(prevSchool !== "—" || prevClass !== "—" || tcNumber !== "—") && <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
              <h3 className="text-md font-bold text-slate-800 border-b border-slate-50 pb-3.5 mb-4 flex items-center gap-2">
                <span><School className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Previous Academic History
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[{
              label: "Previous School Name",
              val: prevSchool
            }, {
              label: "Class Last Studied",
              val: prevClass
            }, {
              label: "Board of Education",
              val: prevBoard
            }, {
              label: "Transfer Certificate (TC) No",
              val: tcNumber
            }].map((f, i) => <div key={i} className="bg-slate-50/50 hover:bg-slate-50 rounded-2xl p-3 border border-slate-100/50 transition-colors">
                    <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{f.label}</div>
                    <div className="text-sm font-semibold text-slate-800 mt-1">{f.val}</div>
                  </div>)}
              </div>
            </div>}

        </div>

        {/* Right Side: Fees Summary & Address Info */}
        <div className="space-y-6">
          
          {/* Fee & Financial Summary */}
          <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
            <h3 className="text-md font-bold text-slate-800 border-b border-slate-50 pb-3.5 mb-4 flex items-center gap-2">
              <span><CreditCard className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Financial Status
            </h3>

            {/* Circular Progress & Pct */}
            <div className="flex items-center gap-4 mb-6 mt-2">
              <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center relative shadow-inner overflow-hidden border border-slate-100">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="32" cy="32" r="26" stroke="#f1f5f9" strokeWidth="4" fill="transparent" />
                  <circle cx="32" cy="32" r="26" stroke="#10b981" strokeWidth="4" fill="transparent" strokeDasharray={2 * Math.PI * 26} strokeDashoffset={2 * Math.PI * 26 * (1 - paidPercentage / 100)} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-[13px] font-extrabold text-emerald-600">
                  {paidPercentage}%
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Fee Collection Ratio</div>
                <div className="text-sm font-extrabold text-slate-800 mt-0.5">{paidPercentage === 100 ? "Fully Paid" : "Outstanding Balance"}</div>
              </div>
            </div>

            {/* Fee amounts */}
            <div className="space-y-3.5 mb-6">
              <div className="flex justify-between items-center bg-slate-50 px-4 py-2.5 rounded-2xl border border-slate-100/50">
                <span className="text-xs font-semibold text-slate-500">Total Term Fee</span>
                <span className="text-sm font-extrabold text-slate-800">₹{totalFee.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center bg-emerald-50 px-4 py-2.5 rounded-2xl border border-emerald-100/50">
                <span className="text-xs font-semibold text-emerald-600">Amount Paid</span>
                <span className="text-sm font-extrabold text-emerald-700">₹{paidFee.toLocaleString()}</span>
              </div>
              <div className={`flex justify-between items-center px-4 py-2.5 rounded-2xl border ${pendingFee > 0 ? "bg-red-50 border-red-100/50 text-red-700" : "bg-green-50 border-green-100/50 text-green-700"}`}>
                <span className="text-xs font-semibold">Outstanding Balance</span>
                <span className="text-sm font-extrabold">₹{pendingFee.toLocaleString()}</span>
              </div>
            </div>

            {/* Term breakdown */}
            <div className="border-t border-slate-50 pt-4 space-y-3">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Term Wise Summary</div>
              {["T1", "T2", "T3"].map((t, idx) => {
              const termData = raw.termFees?.[t] || {};
              const termDue = termData.due || totalFee / 3;
              const termPaid = termData.paid || (idx === 0 && paidFee >= termDue ? termDue : idx === 1 && paidFee > termDue ? paidFee - termDue : 0);
              const termBal = Math.max(termDue - termPaid, 0);
              const tStatus = termBal === 0 ? "PAID" : termPaid > 0 ? "PARTIAL" : "PENDING";
              return <div key={t} className="flex justify-between items-center text-xs p-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${tStatus === "PAID" ? "bg-emerald-500" : tStatus === "PARTIAL" ? "bg-amber-500" : "bg-red-500"}`}></div>
                      <span className="font-bold text-slate-700">Term {idx + 1}</span>
                    </div>
                    <span className="font-bold text-slate-600">₹{termPaid.toLocaleString()} / ₹{termDue.toLocaleString()}</span>
                  </div>;
            })}
            </div>
          </div>

          {/* Address & Residential Details */}
          <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
            <h3 className="text-md font-bold text-slate-800 border-b border-slate-50 pb-3.5 mb-4 flex items-center gap-2">
              <span><MapPin className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Residential Address
            </h3>
            <div className="space-y-4">
              <div>
                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Address Details</div>
                <div className="text-sm font-semibold text-slate-700 leading-relaxed mt-1">{address}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">City</div>
                  <div className="text-sm font-bold text-slate-800 mt-1">{city}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Pincode</div>
                  <div className="text-sm font-bold text-slate-800 mt-1">{pincode}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Remarks Section */}
          {remarks && remarks !== "—" && <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
              <h3 className="text-md font-bold text-slate-800 border-b border-slate-50 pb-3.5 mb-3 flex items-center gap-2">
                <span><FileEdit className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Administrative Remarks
              </h3>
              <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-xl p-3 leading-relaxed">
                {remarks}
              </p>
            </div>}

        </div>

      </div>
    </div>;
}